// ============================================================================
// THE CYCLE — Pitch Tunneling Analysis Card
// ============================================================================
// Shows tunneling scores between pitch pairs, release point similarity,
// and movement differentials.

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, Typography, CircularProgress, Chip, useTheme, alpha,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, LinearProgress,
} from '@mui/material';
import { pitchSplitsApi } from '../services/apiService';

import { getPitchName as pitchLabel, PITCH_COLORS } from '../utils/pitchTypes';

const scoreColor = (score) => {
  if (score >= 80) return '#2ecc71';
  if (score >= 60) return '#f39c12';
  if (score >= 40) return '#e67e22';
  return '#e74c3c';
};

const scoreLabel = (score) => {
  if (score >= 80) return 'Elite';
  if (score >= 65) return 'Good';
  if (score >= 50) return 'Average';
  return 'Poor';
};

// Release point scatter chart — plots pitch types by release X/Z with hover tooltip
const ReleasePointChart = ({ profiles, isDark }) => {
  const [hovered, setHovered] = useState(null);

  const handleEnter = useCallback((e, pt, p) => {
    setHovered({ pt, p, mouseX: e.clientX, mouseY: e.clientY });
  }, []);
  const handleMove = useCallback((e) => {
    setHovered(prev => prev ? { ...prev, mouseX: e.clientX, mouseY: e.clientY } : null);
  }, []);
  const handleLeave = useCallback(() => setHovered(null), []);

  const profileEntries = Object.entries(profiles).filter(
    ([, p]) => p.avgRelX != null && p.avgRelZ != null
  );
  if (profileEntries.length === 0) return null;

  const W = 260, H = 190, pad = 30;

  // Auto-zoom: compute axis range from actual data + 0.12 ft padding
  const rxVals = profileEntries.map(([, p]) => p.avgRelX);
  const rzVals = profileEntries.map(([, p]) => p.avgRelZ);
  const dataXMin = Math.min(...rxVals), dataXMax = Math.max(...rxVals);
  const dataZMin = Math.min(...rzVals), dataZMax = Math.max(...rzVals);
  const xPad = Math.max(0.12, (dataXMax - dataXMin) * 0.35);
  const zPad = Math.max(0.12, (dataZMax - dataZMin) * 0.35);
  const xMin = dataXMin - xPad, xMax = dataXMax + xPad;
  const zMin = dataZMin - zPad, zMax = dataZMax + zPad;

  const sx = (v) => pad + ((v - xMin) / (xMax - xMin)) * (W - pad * 2);
  const sz = (v) => (H - pad) - ((v - zMin) / (zMax - zMin)) * (H - pad * 2);

  // Tick marks spaced nicely within the zoomed range
  const niceStep = (range) => { const s = range / 4; const p = Math.pow(10, Math.floor(Math.log10(s))); return Math.ceil(s / p) * p; };
  const makeTicks = (mn, mx) => { const step = niceStep(mx - mn); const start = Math.ceil(mn / step) * step; const arr = []; for (let v = start; v <= mx + 1e-9; v = Math.round((v + step) * 1e6) / 1e6) arr.push(v); return arr; };
  const ticks = { x: makeTicks(xMin, xMax), z: makeTicks(zMin, zMax) };

  const tooltipW = 160, tooltipH = 140;
  const tooltipLeft = hovered
    ? (hovered.mouseX + tooltipW + 14 > window.innerWidth ? hovered.mouseX - tooltipW - 10 : hovered.mouseX + 12)
    : 0;
  const tooltipTop = hovered
    ? (hovered.mouseY + tooltipH + 14 > window.innerHeight ? hovered.mouseY - tooltipH - 10 : hovered.mouseY + 10)
    : 0;

  return (
    <Box sx={{ position: 'relative' }}>
      <svg width={W} height={H} style={{ display: 'block', overflow: 'visible' }}>
        <rect width={W} height={H} fill={isDark ? '#11151f' : '#f5f7fa'} rx={6} />
        {/* Grid lines */}
        {ticks.x.map(v => (
          <line key={`gx${v}`} x1={sx(v)} y1={pad} x2={sx(v)} y2={H - pad}
            stroke={isDark ? '#1e2535' : '#e0e4ec'} strokeWidth={1} />
        ))}
        {ticks.z.map(v => (
          <line key={`gz${v}`} x1={pad} y1={sz(v)} x2={W - pad} y2={sz(v)}
            stroke={isDark ? '#1e2535' : '#e0e4ec'} strokeWidth={1} />
        ))}
        {/* Axis labels */}
        {ticks.x.filter((_, i) => i % 2 === 1).map(v => (
          <text key={`tx${v}`} x={sx(v)} y={H - 4} textAnchor="middle"
            fill={isDark ? '#556' : '#aaa'} fontSize={8}>{v}</text>
        ))}
        {ticks.z.map(v => (
          <text key={`tz${v}`} x={6} y={sz(v) + 3} textAnchor="middle"
            fill={isDark ? '#556' : '#aaa'} fontSize={8}>{v}</text>
        ))}
        <text x={W / 2} y={H} textAnchor="middle" fill={isDark ? '#778' : '#999'} fontSize={9}>Arm Side ← Release X (ft) → Glove</text>
        {/* Pitch dots */}
        {profileEntries.map(([pt, p]) => {
          const cx = sx(p.avgRelX);
          const cy = sz(p.avgRelZ);
          const color = PITCH_COLORS[pt] || '#888';
          const isHov = hovered?.pt === pt;
          return (
            <g key={pt}
              onMouseEnter={(e) => handleEnter(e, pt, p)}
              onMouseMove={handleMove}
              onMouseLeave={handleLeave}
              style={{ cursor: 'pointer' }}
            >
              <circle cx={cx} cy={cy} r={isHov ? 13 : 9} fill={color} opacity={0.18} />
              <circle cx={cx} cy={cy} r={isHov ? 7 : 5} fill={color}
                stroke={isDark ? '#fff' : '#222'} strokeWidth={isHov ? 2 : 1.5} opacity={0.9} />
              <text x={cx} y={cy - 8} textAnchor="middle" fill={color} fontSize={isHov ? 10 : 9} fontWeight={700}>{pt}</text>
            </g>
          );
        })}
      </svg>
      {/* Fixed-position tooltip */}
      {hovered && (
        <Box sx={{
          position: 'fixed', left: tooltipLeft, top: tooltipTop,
          zIndex: 9999, pointerEvents: 'none',
          minWidth: tooltipW,
          bgcolor: isDark ? alpha('#0d1117', 0.97) : alpha('#fff', 0.97),
          border: `1.5px solid ${alpha(PITCH_COLORS[hovered.pt] || '#888', 0.65)}`,
          borderRadius: 1.5,
          boxShadow: `0 4px 20px ${alpha(PITCH_COLORS[hovered.pt] || '#888', 0.22)}`,
          p: 1.25,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.6, pb: 0.5,
            borderBottom: `1px solid ${alpha(PITCH_COLORS[hovered.pt] || '#888', 0.2)}`
          }}>
            <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: PITCH_COLORS[hovered.pt] || '#888' }} />
            <Typography sx={{ fontWeight: 800, fontSize: '0.8rem', color: PITCH_COLORS[hovered.pt] || 'inherit' }}>
              {pitchLabel(hovered.pt)}
            </Typography>
          </Box>
          {[
            ['Release X', `${hovered.p.avgRelX?.toFixed(2)} ft`],
            ['Release Z', `${hovered.p.avgRelZ?.toFixed(2)} ft`],
            ['Avg Velo', hovered.p.avgVelo ? `${hovered.p.avgVelo.toFixed(1)} mph` : '—'],
            ['Spin Rate', hovered.p.avgSpin ? `${Math.round(hovered.p.avgSpin).toLocaleString()} rpm` : '—'],
            ['H Break', hovered.p.avgHorizBreak != null ? `${hovered.p.avgHorizBreak.toFixed(1)}"` : '—'],
            ['V Break', hovered.p.avgVertBreak != null ? `${hovered.p.avgVertBreak.toFixed(1)}"` : '—'],
            ['Count', hovered.p.count],
          ].map(([label, value]) => (
            <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.15 }}>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontWeight: 600 }}>{label}</Typography>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, fontFamily: '"Roboto Mono", monospace' }}>{value}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const PitchTunnelingCard = ({ team, playerName, season, perspective = 'pitcher', gameType }) => {
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!team || !playerName) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await pitchSplitsApi.getTunneling(perspective, team, playerName, season, { gameType });
        setData(res);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [team, playerName, season, perspective, gameType]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>;
  if (error) return <Typography color="error" variant="body2">{error}</Typography>;
  if (!data?.pairs?.length) return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
      <Typography variant="subtitle1" fontWeight={800}>Pitch Tunneling</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Not enough pitch data for tunneling analysis.</Typography>
    </Card>
  );

  const { pairs, pitchProfiles: profiles, totalPitches } = data;
  const topPairs = pairs.slice(0, 8);
  const isDark = theme.palette.mode === 'dark';

  return (
    <Card elevation={0} sx={{
      border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={800}>Pitch Tunneling</Typography>
        <Chip label={`${totalPitches || 0} pitches`} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Tunneling measures how deceptive pitch pairs are based on similar release points but differing movement.
        Higher scores = better deception.
      </Typography>

      {/* Top Pairs */}
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Best Tunneling Pairs</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
        {topPairs.map((pair, idx) => (
          <Box key={idx} sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, p: 1,
            borderRadius: 1.5,
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            bgcolor: idx < 3 ? alpha(scoreColor(pair.tunnelScore), 0.04) : 'transparent',
          }}>
            <Typography variant="caption" fontWeight={700} sx={{
              width: 20, textAlign: 'center', color: 'text.secondary', fontSize: '0.75rem',
            }}>
              #{idx + 1}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, minWidth: 100 }}>
              <Chip
                label={pitchLabel(pair.pitchA)}
                size="small"
                sx={{
                  height: 22, fontSize: '0.75rem', fontWeight: 700,
                  bgcolor: alpha(PITCH_COLORS[pair.pitchA] || theme.palette.primary.main, 0.15),
                  color: PITCH_COLORS[pair.pitchA] || 'inherit',
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>↔</Typography>
              <Chip
                label={pitchLabel(pair.pitchB)}
                size="small"
                sx={{
                  height: 22, fontSize: '0.75rem', fontWeight: 700,
                  bgcolor: alpha(PITCH_COLORS[pair.pitchB] || theme.palette.primary.main, 0.15),
                  color: PITCH_COLORS[pair.pitchB] || 'inherit',
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(pair.tunnelScore, 100)}
                sx={{
                  height: 8, borderRadius: 1,
                  bgcolor: alpha(theme.palette.divider, 0.15),
                  '& .MuiLinearProgress-bar': {
                    bgcolor: scoreColor(pair.tunnelScore),
                    borderRadius: 1,
                  },
                }}
              />
            </Box>
            <Typography variant="body2" fontWeight={800} sx={{
              minWidth: 40, textAlign: 'right',
              color: scoreColor(pair.tunnelScore),
            }}>
              {pair.tunnelScore.toFixed(0)}
            </Typography>
            <Chip
              label={scoreLabel(pair.tunnelScore)}
              size="small"
              sx={{
                height: 20, fontSize: '0.75rem', fontWeight: 700,
                bgcolor: alpha(scoreColor(pair.tunnelScore), 0.12),
                color: scoreColor(pair.tunnelScore),
              }}
            />
          </Box>
        ))}
      </Box>

      {/* Release Point Chart */}
      {profiles && Object.values(profiles).some(p => p.avgRelX != null) && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Release Point</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Hover each pitch type to see velocity, spin, and movement. Tight cluster = consistent release.
          </Typography>
          <ReleasePointChart profiles={profiles} isDark={isDark} />
        </Box>
      )}

      {/* Pitch Profiles */}
      {profiles && Object.keys(profiles).length > 0 && (
        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Pitch Profiles</Typography>
          <TableContainer>
            <Table size="small" sx={{ '& td, & th': { px: 1, py: 0.5, fontSize: '0.75rem' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Pitch</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Velo</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Spin</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>H Break</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>V Break</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Release</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>N</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(profiles)
                  .sort((a, b) => b[1].count - a[1].count)
                  .map(([pt, p]) => (
                    <TableRow key={pt} sx={{ '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.04) } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: PITCH_COLORS[pt] || '#888' }} />
                          <Typography variant="caption" fontWeight={700}>{pitchLabel(pt)}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">{p.avgVelo?.toFixed(1) || '—'}</TableCell>
                      <TableCell align="center">{p.avgSpin?.toFixed(0) || '—'}</TableCell>
                      <TableCell align="center">{p.avgHorizBreak?.toFixed(1) || '—'}"</TableCell>
                      <TableCell align="center">{p.avgVertBreak?.toFixed(1) || '—'}"</TableCell>
                      <TableCell align="center">
                        ({p.avgRelX?.toFixed(1) || '—'}, {p.avgRelZ?.toFixed(1) || '—'})
                      </TableCell>
                      <TableCell align="center">{p.count}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Card>
  );
};

export default PitchTunnelingCard;
